import IconCommon from '@/components/Common/DesignSystem/Icon/IconCommon';
import { NolPromotionStoreCtx } from '@/stores/NolPromotionStore';
import { getYaNative } from '@yanolja-org/ya-native';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useContext, useMemo } from 'react';

import styles from './index.module.scss';

interface NoticeProps {
  deliberationTitle: string;
  deliberationDescription: string;
}

const Notice: FC<NoticeProps> = ({ deliberationTitle, deliberationDescription }) => {
  const store = useContext(NolPromotionStoreCtx);
  const [isExtended, setExtended] = React.useState(true);

  const { onHYCardLinkClick } = useMemo(() => {
    const onLinkClick = (path) => {
      const yaNative = getYaNative();
      if (yaNative.isEnabled) {
        window.location.href = `yanoljamotel://wv?ya_outlink=true&url=${path}`;
      } else {
        window.open(path, '_blank');
      }
    };
    return {
      onHYCardLinkClick: () => onLinkClick('https://www.hyundaicard.com/cpc/cr/CPCCR0201_01.hc?cardWcd=NOLHC'),
    };
  }, []);

  const onTermsClick = useCallback(() => {
    location.href = store.nolPointTermsUrl;
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.title}>유의사항</div>
      <ul className={clsx(styles.descriptions, styles.mgbt22)}>
        <li>NOL 카드 연회비 : 국내전용/국내외겸용(Visa Platinum/Amex Platinum) 20,000원, 가족 카드 5,000원</li>
        <li className={'bold'}>
          카드 이용대금 연체 시 약정 금리+연체 가산 금리 3%의 연체 금리가 적용됩니다.
          <br />
          (회원별, 이용 상품별 차등 적용/법정 최고 금리 20% 이내)
          <br />
          단, 연체 발생 시점에 약정 금리가 없는 경우 아래와 같이 적용
          <br />- 일시불: 거래 발생 시점 기준 최소 기간(2개월)의 유이자 할부 약정 금리+연체 가산 금리 3%
          <br />- 무이자할부: 거래 발생시점 기준 동일한 할부 계약 기간의 유이자할부 약정금리+연체가산금리 3%
        </li>
        <li className={'bold'}>
          상환 능력에 비해 신용카드 이용금액이 과도할 경우, 귀하의 개인신용평점이 하락할 수 있습니다.
        </li>
        <li className={'bold'}>개인신용평점 하락 시 금융거래와 관련된 불이익이 발생할 수 있습니다.</li>
        <li className={'bold'}>일정 기간 원리금을 연체할 경우, 모든 원리금을 변제할 의무가 발생할 수 있습니다.</li>
        <li>신용카드 발급이 부적정한 경우(연체금 보유, 개인신용평점 낮음 등) 카드 발급이 제한될 수 있습니다.</li>
        <li>카드 이용대금과 이에 수반되는 모든 수수료를 지정된 대금 결제일에 상환합니다.</li>
        <li>
          금융소비자는 금융소비자보호법 제19조 제1항에 따라 해당 상품 또는 서비스에 대하여 설명을 받을 권리가 있습니다.
        </li>
        <li>자세한 내용 및 이용 조건은 카드 신청 전 현대카드 홈페이지 및 상품설명서, 약관 참고</li>
        <li>(주)놀유니버스는 현대카드의 신용카드 회원 모집 업무를 대리·중개합니다.</li>
        <li>(주)놀유니버스는 다수의 신용카드사를 대리하거나 중개합니다.</li>
        <li>(주)놀유니버스는 현대카드의 금융상품에 대한 계약체결권한이 없습니다.</li>
        <li>(주)놀유니버스는 금융관계법률에 따라 현대카드와 위탁계약을 체결한 금융상품판매대리·중개업자입니다.</li>
        <li>
          <p className={styles.mobileBlockPcInlineBlock}>
            {deliberationTitle}&nbsp;{deliberationDescription}
          </p>
        </li>
      </ul>
      <div className={styles.extendButton} onClick={() => setExtended((_isExtended) => !_isExtended)}>
        {isExtended ? '접기' : '펼치기'}
        {isExtended ? <IconCommon type="icon_arrowup_fill" /> : <IconCommon type="icon_arrowdrop_fill" />}
      </div>
      <div className={!isExtended ? styles.folded : ''} style={{ fontSize: '1.2rem', lineHeight: '1.8rem' }}>
        <div className={styles.title}>NOL 카드 쿠폰 유의사항</div>
        <div className={styles.subtitle}>[이벤트 기간]</div>
        <div>{store.eventNolCouponPeriod}</div>
        <div className={styles.subtitle}>[NOL 쿠폰 혜택]</div>
        <div>
          ※ 할인 쿠폰의 할인비율과 최소 결제 금액 조건은 <strong>상품 판매가(포인트 미적용 금액)를 기준</strong>
          으로 하며,{' '}
          <strong>NOL 카드 페이에 등록된 NOL 카드로 NOL 앱/웹에서 결제 시에만 NOL 쿠폰을 사용할 수 있습니다. </strong>
        </div>
        <br />
        <div className={styles.subtitle}>[NOL 카드 웰컴 쿠폰 혜택]</div>
        <div className={clsx(styles.grid, styles.grid5)}>
          <div className={styles.tableTitle}>사용처</div>
          <div className={styles.tableTitle}>혜택</div>
          <div className={styles.tableTitle}>수량</div>
          <div className={styles.tableTitle}>이용조건</div>
          <div className={styles.tableTitle}>비고</div>
          <div style={{ gridColumn: '1', gridRow: '2/7' }}>NOL 앱/웹</div>
          <div>국내숙소 50% 할인 쿠폰</div>
          <div>2장</div>
          <div>
            - 최대 할인 금액 5만원
            <br />- 5만원 이상 결제 시 사용 가능
          </div>
          <div style={{ gridColumn: '5', gridRow: '2/7' }}>
            <strong>특정 상품 이용 불가.</strong> 동일 상품에 대해 본 할인 쿠폰 외 다른 할인 쿠폰은 중복 적용 불가.
          </div>
          <div>해외숙소 50% 할인 쿠폰</div>
          <div>1장</div>
          <div>
            - 최대 할인 금액 5만원
            <br />- 5만원 이상 결제 시 사용 가능
          </div>
          <div>레저 50% 할인 쿠폰</div>
          <div>1장</div>
          <div>- 최대 할인 금액 1만원</div>
          <div>고속버스 50% 할인 쿠폰</div>
          <div>1장</div>
          <div>- 최대 할인 금액 5천원</div>
        </div>
        <div className={styles.separator} />
        <div className={styles.subtitle}>[NOL 카드 실적 쿠폰 혜택]</div>
        <div className={clsx(styles.grid, styles.grid5)}>
          <div className={styles.tableTitle}>사용처</div>
          <div className={styles.tableTitle}>혜택</div>
          <div className={styles.tableTitle}>수량</div>
          <div className={styles.tableTitle}>이용조건</div>
          <div className={styles.tableTitle}>비고</div>
          <div style={{ gridColumn: '1', gridRow: '2/7' }}>NOL 앱/웹</div>
          <div>국내숙소 10% 할인 쿠폰</div>
          <div>1장</div>
          <div>
            - 최대 할인 금액 3만원
            <br />- 10만원 이상 결제 시 사용 가능
          </div>
          <div style={{ gridColumn: '5', gridRow: '2/7' }}>
            <strong>특정 상품 이용 불가.</strong> 동일 상품에 대해 본 10% 할인 쿠폰 외 다른 할인 쿠폰은 중복 적용 불가.
          </div>
          <div>해외숙소 10% 할인 쿠폰</div>
          <div>1장</div>
          <div>
            - 최대 할인 금액 1.5만원 <br />- 10만원 이상 결제 시 사용 가능
          </div>
          <div>렌터카 10% 할인 쿠폰</div>
          <div>1장</div>
          <div>- 최대 할인 금액 5천원</div>
          <div>고속버스 10% 할인 쿠폰</div>
          <div>1장</div>
          <div>- 최대 할인 금액 5천원</div>
          <div>레저 10% 할인 쿠폰</div>
          <div>1장</div>
          <div>- 최대 할인 금액 5천원</div>
        </div>
        <div className={styles.subtitle}>[NOL 카드 쿠폰 종류 및 발행조건]</div>
        <div className={clsx(styles.grid, styles.grid52)}>
          <div className={styles.tableTitle}>쿠폰명</div>
          <div className={styles.tableTitle}>발행 조건</div>
          <div className={styles.tableTitle}>발행 주기</div>
          <div className={styles.tableTitle}>발행 시점</div>
          <div className={styles.tableTitle}>사용 기간</div>
          <div>NOL 카드 웰컴 쿠폰</div>
          <div>
            - NOL 카드 페이에 NOL 카드 최초 등록 후 30일 이내 NOL 회원으로 가입 시 쿠폰 자동 발행
            <br />- NOL 카드 교체 · 해지 및 재발급하여 재등록 시 이벤트 대상에서 제외
          </div>
          <div>최초 1회</div>
          <div>조건 부합시 쿠폰함에 자동 지급</div>
          <div>쿠폰 발급일로부터 30일</div>
          <div>NOL 카드 실적 쿠폰</div>
          <div>- NOL 카드 전월 이용 실적 40만원 이상 달성 고객</div>
          <div>매월 1회</div>
          <div>당월 6일 순차 발급</div>
          <div>당월 6일 ~ 익월 5일</div>
        </div>
        <div className={styles.subtitle}>[NOL 카드 쿠폰 유의사항]</div>
        <ul className={styles.descriptions}>
          <li>
            <strong>
              NOL 카드 웰컴쿠폰은 NOL 카드 페이에 NOL 카드 최초 등록 후 30일 이내 NOL 회원으로 가입 시 제공됩니다.
              <br />
              (단,NOL 카드 본인 회원에 한해 1회 혜택 제공 / NOL 카드 교체ᆞ해지 및 재발급 하여 재등록시 이벤트 대상에서
              제외)
            </strong>
          </li>
          <li>
            <strong>NOL 카드 실적쿠폰은 NOL 카드 전월 이용 실적 40만원 이상 달성 시 제공됩니다.</strong>
          </li>
          <li>
            <strong>조건 충족 시, NOL 플랫폼에 가입된 본인 회원 계정의 쿠폰함으로 자동 제공</strong>되며, NOL 실적
            쿠폰은 당월 6일 영업 시간 내 순차적으로 발급됩니다. 예시) NOL 회원은 NOL 앱/웹 쿠폰함에서 확인 가능
          </li>
          <li>
            이용 실적에 따라 제공되는 할인 쿠폰은 결제 취소 등으로 실적이 충족되지 않을 경우 사용이 제한될 수 있습니다.
          </li>
          <li>
            카테고리별 NOL 카드 쿠폰은 별개 상품에 동시 사용 가능합니다. 단, 한 상품에 대해 1개의 쿠폰만 적용
            가능합니다.
            <br />
            예시) 국내 호텔 상품(숙소)과 테마파크 입장권(레저) 동시 구매 시, 국내숙소 NOL 쿠폰과 레저 NOL 쿠폰 모두 사용
            가능
          </li>
          <li>예약 시 사용된 쿠폰은 취소시 쿠폰함으로 반환되며, 쿠폰 유효기간이 남아있을 경우 재사용이 가능합니다.</li>
          <li>쿠폰에서 제공하는 혜택 금액보다 결제 금액이 작을 경우, 차액 환불이 불가능합니다.</li>
          <li>NOL 쿠폰은 특정 상품에 사용이 제한될 수 있습니다.</li>
          <li>NOL 카드 해지 시에 NOL 쿠폰은 일괄 회수 처리됩니다.</li>
          <li>NOL 카드 쿠폰은 마이페이지 &gt; NOL 쿠폰 &gt; 쿠폰함 내에서 확인 가능합니다.</li>
          <li>
            NOL 카드 쿠폰 (웰컴/실적)은 NOL / 트리플에서 제공하는 NOL 카드 혜택으로 사전 안내 후 변경될 수 있습니다.
          </li>
          <li>트리플에서 제공하는 NOL 쿠폰의 상세 안내는 트리플에서 확인 가능합니다.</li>
          <li>사용·소멸 등에 대한 안내는 각 플랫폼 홈페이지 또는 아래 고객센터로 문의해 주세요.</li>
          <li>
            <ul>
              <li>NOL 고객센터 :1644-1346 / 카카오톡 플러스친구 @NOL고객센터</li>
              <li>트리플 고객센터 : 1588-2539</li>
            </ul>
          </li>
        </ul>
        <br />
        <div className={styles.subtitle}>[공통 유의사항]</div>
        <ul className={styles.descriptions}>
          <li>
            <strong>NOL 카드 쿠폰 이벤트는 {store.eventNolCouponPeriod}까지 진행합니다.</strong>&nbsp; 단, 본 이벤트는
            당사 또는 제휴사 사정에 의해 변경 또는 중단 될 수 있습니다.
          </li>
          <li>
            혜택 제공 시점에 대상 카드 해지, 분실, 교체(재발급 등), 정지(연체 등) 등의 경우 혜택이 제공되지 않습니다.
            (카드 상태가 정상인 경우에 한해 제공)
          </li>
          <li>
            <u className={styles.underline}>
              이벤트 혜택(포인트, 캐시백, 쿠폰 등)을 제공받은 달부터 13개월 내 대상 카드 해지 또는 탈회 시 받은 혜택은
              현금으로 청구되거나, 등록된 본인 명의 결제 계좌에서 자동 출금될 수 있음
              <br />
              (예시 : 2026년 4월에 혜택을 제공받은 후, 2027년 4월 30일 이전에 대상 카드 해지 또는 탈회 시 제공받은 혜택
              금액만큼 청구)
            </u>
          </li>
          <li>
            혜택 제공월 포함 12개월 내 이벤트 채널에 관계없이 현대카드의 다른 이벤트 혜택(캐시백, 포인트, 할인 쿠폰
            등)을 받은 이력이 있는 경우, 조건을 충족하더라도 혜택 제외
          </li>
          <li>
            카드 이용 실적에 관한 상세 내용은 현대카드 홈페이지(
            <span className={styles.link} onClick={onHYCardLinkClick}>
              www.hyundaicard.com
            </span>
            ) 참고
          </li>
        </ul>
        <div className={styles.separator} />

        {/* NOL 포인트 긴급적립 유의사항 */}
        <div className={styles.title}>첫 결제 캐시백 유의사항</div>
        <div className={styles.subtitle}>[첫 결제 캐시백 안내]</div>
        <ul className={styles.descriptions}>
          <li>
            <b>이벤트 기간 : {store.eventPeriod}</b>
          </li>
          <li>
            <b>혜택 : 2만원 캐시백</b> <br />: 이벤트 기간 직전 6개월 간(2025.10.28 ~ 2026.04.27) NOL 카드 결제 이력이
            없는 NOL 카드 보유 회원이 NOL 카드 페이에 등록된 NOL 카드로 이벤트 기간 동안 NOL에서 단건 2.1만원 이상 첫
            결제 시 제공
          </li>
          <li>
            <b>혜택 적용 : 조건 충족 시 26년 7월 말일 경, 본인 명의 현대카드 결제 계좌로 지급</b>
          </li>
        </ul>
        <br />
        <div className={styles.subtitle}>[첫 결제 캐시백 유의사항]</div>
        <ul className={styles.descriptions}>
          <li>NOL 본인인증 회원에 한해 최초 1회 제공</li>
          <li>NOL 카드 보유 회원 대상에 한해 혜택 적용(본인 회원 기준, 1회 제공)</li>
          <li>
            항공권 결제 시에는 NOL 카드페이 미이용시에도 혜택 적용됨 (단,에어서울, 이스타 항공 국내선 항공권은 캐시백
            제공 불가)
          </li>
          <li>
            NOL 카드 첫 결제 혜택은 NOL/NOL 인터파크(NOL 인터파크투어/NOL 티켓)/트리플 내 본인 회원 기준 1회 제공되며,
            기간 내 첫 결제를 기준으로 혜택 적용됨
          </li>
          <li>NOL 카드 페이가 아닌 간편결제 서비스(카카오페이, 네이버페이, PAYCO 등)로 결제 시 혜택 제공 불가</li>
          <li>캐시백 제공 일정은 현대카드 또는 회원 사정에 따라 변경될 수 있음</li>
          <li>
            <b>아래 이벤트에 참여한 경우 본 혜택 이벤트 참여 불가</b>
            <br />- 23년 4월 18일부터 26년 4월 27일까지 진행된 NOL 카드 전용 이벤트 (캐시백, 청구할인, 골드클래스,
            스키시즌권, 뮤지컬&전시 할인 쿠폰, NOL 웰컴 쿠폰 등)
            <br />- 26년 4월 28일부터 6월 22일까지 진행되는 NOL 카드 에버랜드 첫 결제 캐시백 이벤트
          </li>
          <li>
            <b>
              혜택 제공 시점으로부터 13개월 내 NOL 카드 해지 및 탈회 시 제공 받은 혜택 금액이 현대카드를 통해서 현금으로
              청구될 수 있음
            </b>
          </li>
          <li>다른 NOL 카드 혜택 (NOL 카드 캐시백, NOL 카드 할인 쿠폰 등)과 중복 제공되지 않음</li>
          <li>
            현대카드의 다른 이용 유도 프로모션에 참여했거나 혜택(첫 결제, 포인트, 할인 쿠폰 등)을 받은 경우, 조건을
            충족하더라도 혜택 중복 적용 불가
          </li>
          <li>NOL 포인트 10% 기본 적립 혜택과 중복 제공</li>
          <li>
            타 쿠폰 및 즉시 할인, 포인트와 중복 사용 가능(단, 할인 쿠폰 및 즉시 할인, 포인트 사용 후 최종 카드 결제
            금액이 첫 결제 조건 금액 충족 시 혜택 제공)
          </li>
          <li>
            NOL/NOL 인터파크(NOL 인터파크투어/NOL 티켓)/트리플에서 간편결제(NOL 카드 페이)에 등록된 NOL 카드로 첫 결제
            시 혜택 제공
          </li>
          <li>카드 이용 대금 결제일과 캐시백 제공일이 동일한 경우, 캐시백 금액이 이용 대금으로 납부될 수 있음</li>
          <li>카드 승인 후 취소, 부분취소, 반품, 품절, 환불 등으로 결제 금액 미달 시 혜택 제공 불가</li>
          <li>
            혜택 제공 시점에 대상 카드 해지, 분실, 교체(재발급 등), 정지(연체 등) 등의 경우 혜택 제공 불가(카드 상태가
            정상인 경우에 한해 제공)
          </li>
          <li>
            모든 가맹점은 현대카드 가맹점등록 및 업종 분류 기준이며, 가맹점명이 NOL/NOL 인터파크(NOL 인터파크투어/NOL
            티켓)/트리플인 경우에만 혜택이 적용 됨
          </li>
          <li>이외 자세한 내용은 현대카드 고객센터(1577-6000)로 문의</li>
        </ul>
        <br />
        <div className={styles.subtitle}>[공통 유의사항]</div>
        <ul className={styles.descriptions}>
          <li>해당 이벤트는 회사 및 제휴사 등의 사정에 따라 변경 또는 종료될 수 있습니다.</li>
          <li>
            위 이벤트 혜택을 적용받고자 하실 경우 해당 이벤트가 진행되는 회사 서비스(NOL)의 앱/웹 회원으로 가입하여야
            하며, NOL에서 본인인증 회원에 한해 최초 1회 제공됩니다.
          </li>
        </ul>
        <div className={styles.separator} />

        {/* NOL 포인트 긴급적립 유의사항 */}
        <div className={styles.title}>NOL 포인트 긴급적립 유의사항</div>
        <div className={styles.subtitle}>[NOL 포인트 긴급적립 안내]</div>
        <ul className={styles.descriptions}>
          <li>
            <b>혜택 적용 : 현대카드 홈페이지 및 앱에서 NOL 포인트 긴급적립 신청 후 혜택 제공</b>
          </li>
          <li>
            <b>대상 카드 : NOL 카드</b> <br />
            *본인 카드만 신청 가능, 가족카드 불가
          </li>
          <li>
            신청 NOL 포인트 : 10만/20만/30만
            <br />
            NOL 포인트 중 택1
          </li>
          <li>상환 기간 : 24개월</li>
          <li>
            단, NOL· NOL 인터파크(NOL 인터파크투어/NOL 티켓) · 트리플 정책에 따라, 국내선 항공권 등 일부 카테고리 및
            상품의 경우 NOL 포인트 사용 불가
          </li>
        </ul>
        <br />
        <div className={styles.subtitle}>[NOL 포인트 긴급적립 유의사항]</div>
        <ul className={styles.descriptions}>
          <li>
            해당 서비스는 NOL 카드 본인 회원에 한해 현대카드 홈페이지 및 앱에서 신청 가능합니다.(가족 카드 신청 불가)
          </li>
          <li>
            신청 후, 대상카드를 정상 보유한 회원에 한해 서비스가 제공됩니다.(정지 (연체 등), 해지 및 탈회 시 혜택 제외)
          </li>
          <li>
            현대카드 세이브(세이브-오토, 블루 세이브-오토, M 긴급적립, X 긴급할인, 이마트 가전 세이브, 대한항공 마일리지
            긴급적립, 멤버십 리워즈(MR) 긴급적립 등)와 중복 이용할 수 없습니다.
            <br />타 현대카드 세이브 이용 중 NOL 포인트 긴급적립을 이용하시려면 해당 현대카드 세이브 서비스를 상환
            완료해야 합니다.
          </li>
          <li>NOL 포인트 긴급적립 기존 이용 회원은 서비스 정상 종료 또는 약정 기간 만료 이후 재신청 가능합니다.</li>
          <li>
            NOL 포인트 긴급적립은 신청 후 신청 익일 ~ 3영업일 이내 적립되며, 적립일로부터 NOL 카드(본인+가족카드) 이용에
            따라 적립되는 NOL 포인트로 상환됩니다.
          </li>
          <li>
            서비스 신청 전 적립 된 혹은 적립 예정인 NOL 포인트로 상환 불가합니다. (프로모션 적립 및 서비스 신청 전 카드
            이용에 따라 적립된 NOL 포인트로 상환 불가)
          </li>
          <li>서비스 신청 일자 기준 당일(23시 59분까지)에 한해 취소 가능하며, 당일 외 취소 불가합니다.</li>
          <li>
            상환 기간은 긴급적립 포인트 지급일로부터 최대 24개월로 약정되며, 기간 변경 및 연장 불가합니다.
            <br />- 단, 상환 기간 만료 전이라도 카드 이용에 따라 적립된 NOL 포인트로 모두 상환한 경우, 남은 기간과
            관계없이 재신청 가능
          </li>
          <li>
            약정 기간 내 상환하지 못한 NOL 포인트 또는 상환 완료 후 카드 결제 취소로 기존 상환한 NOL 포인트 적립이
            취소될 경우, 해당 NOL 포인트에 대해서는 현대카드 정책에 따라 현금으로 환산해, 상환 기간 만료 다음 달 카드
            이용 대금에 합산 청구됩니다.(1 NOL 포인트=1원)
          </li>
          <li>
            상환 기간 중 아래에 해당하는 경우 서비스 이용 잔액 일시 청구(기한 이익 상실)되며, 약정된 기간 만료 후 재신청
            가능합니다.
            <br />- NOL 포인트 긴급적립 이용 가능 카드 미보유 시(NOL 카드 해지/탈회, 갱신 거부 시)
            <br />- 연속 6개월 이상 상환을 위한 NOL 포인트 미적립 시
            <br />- 연속 2회차 이상 카드 이용 대금 연체 시
          </li>
          <li>
            NOL 포인트 긴급적립 이용 약관에 따라 이용 잔액 일시 청구 시 상환하지 않을 경우, 잔액 원금에 대해 현대카드가
            정한 연체 금리(거래 발생 시점 최소 기간(2개월)의 유이자 할부 약정 금리 + 연체 가산 금리 3%, 법정 최고 금리
            20% 이내)를 적용해 현금 청구됩니다.
          </li>
          <li>
            NOL 포인트 긴급적립은 현대카드의 영업 정책이나 제휴사의 사정에 따라 변경 또는 중단될 수 있으며, 변경 또는
            중단 시 사전 안내됩니다.
          </li>
        </ul>

        <div className={styles.separator} />

        {/* NOL 포인트 유의사항 */}
        <div className={styles.title}>NOL 포인트 유의사항</div>
        <div className={styles.subtitle}>[10% NOL 포인트 적립]</div>
        <ul className={styles.descriptions}>
          <li>
            <strong>
              NOL 포인트란? NOL·NOL인터파크(NOL 인터파크투어/NOL 티켓)·트리플의 사용처에서 상품 결제에 쓸 수 있는
              포인트입니다.
            </strong>
            <br />- 지급된 NOL 포인트는 [NOL App/Web -{'>'} 마이페이지]에서 확인 가능합니다.
          </li>
          <li>NOL 포인트 유효 기간 / NOL 포인트는 적립월 기준 5년 내 사용 가능하며, 유효 기간 이후 소멸 됩니다.</li>
          <li>
            NOL 포인트 상세 안내 / NOL 포인트에 관한 자세한 내용은 [
            <span className={styles.link} onClick={onTermsClick}>
              NOL 포인트 정책
            </span>
            ]을 참고해 주세요.
          </li>
        </ul>
        <div className={clsx(styles.grid, styles.grid4)}>
          <div className={styles.tableTitle}>적립처</div>
          <div className={styles.tableTitle}>적립혜택</div>
          <div className={styles.tableTitle}>대상점</div>
          <div className={styles.tableTitle}>비고</div>
          <div>
            <strong>NOL · NOL 인터파크(NOL 인터파크투어/NOL 티켓) · 트리플</strong>
          </div>
          <div>일상에서 여행까지 즐길 수 있는 3개의 플랫폼에서 이용 금액의 10% 적립</div>
          <div>NOL · NOL 인터파크(NOL 인터파크투어/NOL 티켓) · 트리플</div>
          <div>전월 이용 금액 40만원 이상 시 월 1만, 80만원 이상 시 월 1만 2천 포인트 한도 내 적립</div>
          <div>
            <strong>커피전문점 · 편의점 · 디지털 콘텐츠 서비스</strong>
          </div>
          <div>일상 생활을 즐길 수 있는 3개 업종에서이용 금액의 10% 적립</div>
          <div>
            · 커피전문점 : 스타벅스, 투썸플레이스, 폴 바셋, 이디야, 커피빈
            <br />
            · 편의점 : GS25, CU, 이마트24, 세븐일레븐
            <br />· 디지털 콘텐츠 서비스 : 넷플릭스, 유튜브 프리미엄, 디즈니+, 왓챠, 멜론, 지니, 리디
          </div>
          <div>전월 이용 금액 40만원 이상 시 월 5천, 80만원 이상 시 월 8천 포인트 한도 내 적립</div>
          <div>
            <strong>쇼핑 · 영화 · 골프 · 대중교통</strong>
          </div>
          <div>여가 생활을 즐길 수 있는 4개 업종에서이용 금액의 10% 적립</div>
          <div>
            · 프리미엄아울렛 : 현대프리미엄아울렛, 롯데프리미엄아울렛, 신세계프리미엄아울렛, 스타필드
            <br />
            · 영화 : CGV, 롯데시네마, 메가박스
            <br />
            · 골프 : 골프장/골프연습장(업종)
            <br />· 대중교통 : 시내버스, 지하철
          </div>
          <div>전월 이용 금액 40만원 이상 시 월 5천, 80만원 이상 시 월 8천 포인트 한도 내 적립</div>
          <div>
            <strong>해외 가맹점</strong>
          </div>
          <div>해외 온·오프라인 이용 금액의 10% 적립</div>
          <div>해외 온·오프라인 가맹점</div>
          <div>전월 이용 금액 40만원 이상 시 월 5천, 80만원 이상 시 월 8천 포인트 한도 내 적립</div>
        </div>
        <div className={styles.subtitle}>[NOL 포인트 적립 유의사항]</div>
        <ul className={styles.descriptions}>
          <li>
            <strong>전월 이용 금액 40만원 미만 시 적립 제외</strong>
          </li>
          <ul className={styles.descriptions}>
            <li>
              <strong>전월 이용 금액 산정 기준은 NOL 카드 가이드북 ‘혜택 제공 기준’ 참고</strong>
            </li>
          </ul>
          <li>
            <strong>모든 가맹점은 현대카드 가맹점 등록 및 업종 분류 기준</strong>
          </li>
          <li>
            NOL 인터파크 이용 시 NOL 인터파크 공식 웹/앱 내 ‘NOL 인터파크투어/NOL 티켓’ 카테고리 또는 각 전용 웹/앱 (NOL
            인터파크투어, NOL 티켓) 결제 건에 한해 적립
          </li>
          <li>
            가맹점명이 아닌 간편결제명(네이버페이 등) 또는 별도 전용 가맹점번호로 승인되는 경우 적립 제외. NOL 카드
            페이에 등록된 NOL 카드 결제 건은 적립 가능
            <br />- 예시 : NOL 웹/앱에서 결제 시 가맹점명이 ‘네이버파이낸셜’로 승인되는 경우 적립 제외
          </li>
          <li>
            커피전문점은 백화점·할인점·쇼핑몰 내 입점매장 및 공동가맹점, 온라인 기프트 카드,선물하기 등은 적립 제외
          </li>
          <li>편의점은 현장 결제 시에 한하며, 온라인(제휴사 홈페이지 또는 앱) 결제 시 적립 제외</li>
          <li>
            디지털 콘텐츠 서비스 업종은 공식 홈페이지 및 앱에서 결제 시에만 적립되며, 통신요금 내 합산 청구 또는 애플
            앱스토어·구글 플레이 스토어 결제(인앱결제) 등 가맹점 구분이 불가한 경우 적립 제외
          </li>
          <li>
            영화 업종은 현장 또는 공식 홈페이지 및 앱에서 티켓 결제 시에만 적립되며, 예매 대행 사이트 결제 건, 매점 이용
            금액은 적립 제외
          </li>
          <li>
            단, NOL 인터파크로 영화예매 결제 시 NOL·NOL 인터파크(NOL 인터파크투어/NOL 티켓) · 트리플 영역으로 적립 적용
          </li>
          <li>해외 온·오프라인 가맹점에서 원화로 결제 시 적립 제외</li>
          <li>국제브랜드 수수료 및 해외서비스 수수료는 전월 이용 금액에 포함되지 않으며 적립제외</li>
          <li>
            해외 가맹점 이용 시 별도의 수수료가 부과되니, 국제브랜드 수수료 및 해외서비스 수수료 관련 자세한 내용은 본
            가이드북 ‘카드 이용 유의사항’ 참고
          </li>
          <li>
            NOL·NOL 인터파크(NOL 인터파크투어/NOL 티켓) · 트리플 결제 건 중 적립 제외 대상에 대한 자세한 내용은 현대카드
            홈페이지(
            <span className={styles.link} onClick={onHYCardLinkClick}>
              www.hyundaicard.com
            </span>
            ) 참고
          </li>
          <li>
            적립 제외 : 상품권 등 현금성 유가증권 구매 및 선불카드 구매·충전 금액, 매출 취소 금액, 당사의 모든 할인
            서비스 및 무이자 할부 이용 금액
          </li>
        </ul>
        <div className={styles.separator} />

        {/* 공항라운지 유의사항 유의사항 */}
        <div className={styles.title}>공항라운지 유의사항</div>
        <div className={styles.subtitle}>[공항라운지 연 2회 무료]</div>
        <ul className={styles.descriptions}>
          <li>NOL 카드 국내외겸용(Visa Platinum / Amex Platinum) 전용</li>
          <li>
            대상자: 직전 3개월 간 NOL · NOL 인터파크(NOL 인터파크투어/NOL 티켓)·트리플 내 해외 여행 상품 100만원 이상
            이용 시 제공
          </li>
          <li>이벤트 혜택: 국내 공항 라운지 무료 이용(월 1회, 연 2회 한도)</li>
          <li>서비스 유효기간: 2026년 12월 31일까지</li>
        </ul>
        <br />
        <div className={styles.subtitle}>[공항라운지 혜택 유의사항]</div>
        <ul className={styles.descriptions}>
          <li>국내외겸용카드를 보유한 본인 회원에 한해, 무료 입장 가능</li>
          <li>서비스 이용 시 NOL 카드 실물 카드 제시 필요</li>
          <li>서비스 이용 전 대상 공항(인천/김포) 및 대상 라운지 위치, 영업 시간 확인 후 이용</li>
          <li>
            NOL · NOL 인터파크(NOL 인터파크투어/NOL 티켓)·트리플 가맹점 및 자체 간편결제 서비스(NOL카드 페이)가 아닌
            간편결제 서비스명(네이버페이 등) 또는 별도 가맹점 번호로 결제되는 경우 실적 제외
            <br />- 예시 : NOL에서 결제했으나 가맹점명이 ‘네이버 파이낸셜’인 경우
          </li>
          <li>
            해당 서비스는 Visa 및 Amex에서 제공하는 선택제공 서비스로 이용 관련 자세한 내용은 해당 브랜드사 및 현대카드
            홈페이지 참조
            <ul>
              <li>
                Visa 홈페이지 :{' '}
                <a className={styles.link} href="https://www.visakorea.com">
                  www.visakorea.com
                </a>
              </li>
              <li>
                Amex 홈페이지 :{' '}
                <a className={styles.link} href="https://www.americanexpress.com/kr">
                  www.americanexpress.com/kr
                </a>
              </li>
              <li>
                현대카드 홈페이지 :{' '}
                <a className={styles.link} href="https://www.hyundaicard.com">
                  www.hyundaicard.com
                </a>
              </li>
            </ul>
          </li>
        </ul>
        <div className={styles.separator} />

        <div className={styles.title}>NOL 앱/웹 프로모션 유의사항</div>
        <ul className={styles.descriptions}>
          <li>본 이벤트는 NOL 회원 대상으로 진행되며, NOL 회원으로 로그인 시에만 혜택 지급 및 사용이 가능합니다.</li>
          <li>본 이벤트는 당사 또는 제휴사 사정에 의해 변경 또는 중단될 수 있습니다.</li>
          <li>
            이벤트 관련 문의사항은 &lsquo;NOL 고객센터 1644-1346&rsquo; 또는 &lsquo;카카오톡 플러스친구
            @NOL고객센터&rsquo;로 연락주시기 바랍니다.
          </li>
        </ul>
        <div className={styles.separator} />
        <div className={styles.title}>NOL 인터파크(NOL 인터파크투어/NOL 티켓) · 트리플 프로모션 유의사항</div>
        <ul className={styles.descriptions}>
          <li>본 이벤트는 당사 또는 제휴사 사정에 의해 변경 또는 중단될 수 있습니다.</li>
          <li>
            NOL 인터파크(NOL 인터파크투어/NOL 티켓) · 트리플에서 제공하는 프로모션 상세 안내는 각 플랫폼에서 확인
            가능합니다.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default observer(Notice);
